import { Component, effect, input, output, signal, inject } from '@angular/core';

import { AbstractControl, FormControl, FormGroup, Validators, FormsModule, ReactiveFormsModule } from '@angular/forms';

import { UnsavedChangesService } from 'src/app/shared/services/unsaved-changes.service';
import { ProcessedAnswers, ProcessedAnswersWithForm } from 'src/app/tga/shared/models/models';

import { TgaInputType, TGAQuestion } from '../../../models/question-models';
import { GenericFormHeaderSectionComponent } from '../../../form-header-section/generic-form-header-section/generic-form-header-section.component';
import { QuestionsListFormComponent } from '../../questions-list-form/questions-list-form.component';
import { updateDisabledQuestions } from '../../question-helpers';

@Component({
  selector: 'app-base-generic-questionnaire',
  templateUrl: './base-generic-questionnaire.component.html',
  imports: [GenericFormHeaderSectionComponent, QuestionsListFormComponent, FormsModule, ReactiveFormsModule],
})
export class BaseGenericQuestionnaireComponent {
  private readonly unsavedChangesService = inject(UnsavedChangesService);

  title = input.required<string>();
  questions = input.required<TGAQuestion<TgaInputType>[]>();
  cropYear = input.required<number | null>();
  readOnly = input(false);
  answersUpdated = output<ProcessedAnswersWithForm>();

  readonly formReady = signal(false);
  formGroup: FormGroup = new FormGroup({});

  constructor() {
    effect(() => {
      const questions = this.questions();
      this.buildForm(questions);
    });

    effect(() => {
      const questions = this.questions();
      if (!this.formReady()) return;

      if (this.readOnly()) {
        this.formGroup.disable({ emitEvent: false });
      } else {
        this.formGroup.enable({ emitEvent: false });
        updateDisabledQuestions(questions, this.formGroup);
      }
    });
  }

  emitAnswersUpdated(formGroup: FormGroup): void {
    if (this.readOnly()) return;

    const formValues = formGroup.value;

    this.markFormGroupTouched(formGroup);

    const processedAnswers = this.processFormAnswers(formValues);
    this.unsavedChangesService.registerForm(formGroup);
    this.answersUpdated.emit({
      answers: processedAnswers,
      formGroup: formGroup,
    });
  }

  private buildForm(questions: TGAQuestion<TgaInputType>[]): void {
    if (questions.length === 0) return;

    const group: { [key: string]: AbstractControl } = {};

    for (const question of questions) {
      let initialValue: TgaInputType | Date | undefined = question.value;

      if (question.controlType === 'date-year' && typeof question.value === 'number') {
        initialValue = new Date(question.value, 0, 1);
      }

      const control = new FormControl(initialValue);
      if (question.required) {
        control.addValidators(Validators.required);
      }
      group[question.key] = control;
    }

    this.formGroup = new FormGroup(group);
    this.unsavedChangesService.registerForm(this.formGroup);
    this.formReady.set(true);
  }

  private processFormAnswers(formValues: Record<string, unknown>): ProcessedAnswers {
    const processedAnswers: ProcessedAnswers = {};

    for (const question of this.questions()) {
      const value = formValues[question.key];

      switch (question.controlType) {
        case 'dropdown':
        case 'radio-button': {
          processedAnswers[question.key] = value !== null && value !== undefined ? (value as string) : '';
          break;
        }
        default: {
          processedAnswers[question.key] = value !== null && value !== undefined ? (value as string) : '';
        }
      }
    }

    return processedAnswers;
  }

  private markFormGroupTouched(formGroup: FormGroup): void {
    for (const key of Object.keys(formGroup.controls)) {
      const control = formGroup.get(key);
      if (control) {
        control.markAsTouched();

        if (control instanceof FormGroup) {
          this.markFormGroupTouched(control);
        }
      }
    }
  }
}
